import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, CircularProgress, Chip, Button, Grid,
  Table, TableBody, TableRow, TableCell, TableHead, TableContainer,
  TableSortLabel, ToggleButton, ToggleButtonGroup, alpha, useTheme,
  TextField, InputAdornment
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { searchCommittees } from '../services/api';
import ExportCSVButton from './ExportCSVButton';
import useTableSort from '../hooks/useTableSort';

// Raw sort keys for the sortable committee-list columns.
const COMMITTEE_SORT_KEYS = {
  Committee: (c) => (c.CommitteeName || '').toLowerCase(),
  Chamber: (c) => c.CommitteeChamber || '',
  Type: (c) => c.CommitteeType || '',
  Members: (c) => Number(c.CurrentMemberCount || c.MemberCount || 0),
  Status: (c) => (c.Current ? 1 : 0),
};

export default function CommitteesSearch() {
  const theme = useTheme();
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('current'); // 'current', 'all'
  const [chamberFilter, setChamberFilter] = useState('all'); // 'all', 'House', 'Senate', 'Joint'
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchCommittees = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch committees in batches to get all of them
        const allHits = [];
        let page = 1;
        const perPage = 100;
        let hasMore = true;

        while (hasMore) {
          const data = await searchCommittees('*', page, perPage);
          if (data.hits && data.hits.length > 0) {
            allHits.push(...data.hits);
            hasMore = allHits.length < data.total;
            page++;
          } else {
            hasMore = false;
          }
          // Safety limit to prevent infinite loops
          if (page > 10) break;
        }

        // Sort by name alphabetically
        const sorted = allHits.sort((a, b) =>
          (a.CommitteeName || '').localeCompare(b.CommitteeName || '')
        );
        setCommittees(sorted);
      } catch (err) {
        console.error('Failed to fetch committees:', err);
        setError('Failed to load committees data');
      }
      setLoading(false);
    };
    fetchCommittees();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getChamberColor = (chamber) => {
    if (chamber === 'Senate') return theme.palette.error.main;
    if (chamber === 'House') return theme.palette.success.main;
    return theme.palette.primary.main; // Joint
  };

  // Filter committees based on current selections
  const filteredCommittees = committees.filter(c => {
    // Current/All filter
    if (viewMode === 'current' && !c.Current) return false;

    // Chamber filter
    if (chamberFilter !== 'all' && c.CommitteeChamber !== chamberFilter) return false;

    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const name = (c.CommitteeName || '').toLowerCase();
      const type = (c.CommitteeType || '').toLowerCase();
      if (!name.includes(query) && !type.includes(query)) return false;
    }

    return true;
  });

  // User-sortable headers (#134); default order (name asc) is unchanged
  // until a header is clicked.
  const { order, orderBy, sortedRows, handleSort } = useTableSort(
    filteredCommittees,
    (c, col) => (COMMITTEE_SORT_KEYS[col] ? COMMITTEE_SORT_KEYS[col](c) : ''),
  );
  const SortableTh = ({ col, sx }) => (
    <TableCell sx={{ fontWeight: 600, ...sx }} sortDirection={orderBy === col ? order : false}>
      {COMMITTEE_SORT_KEYS[col] ? (
        <TableSortLabel
          active={orderBy === col}
          direction={orderBy === col ? order : 'asc'}
          onClick={() => handleSort(col)}
        >
          {col}
        </TableSortLabel>
      ) : col}
    </TableCell>
  );

  // Count stats
  const currentCount = committees.filter(c => c.Current).length;
  const houseCount = filteredCommittees.filter(c => c.CommitteeChamber === 'House').length;
  const senateCount = filteredCommittees.filter(c => c.CommitteeChamber === 'Senate').length;
  const jointCount = filteredCommittees.filter(c => c.CommitteeChamber === 'Joint').length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GroupsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">Parliamentary Committees</Typography>
            <Typography variant="body2" color="text.secondary">
              Australian Federal Parliament committee system
            </Typography>
          </Box>
        </Box>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, v) => v && setViewMode(v)}
          size="small"
        >
          <ToggleButton value="current">Current ({currentCount})</ToggleButton>
          <ToggleButton value="all">All Committees</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: chamberFilter === 'all' ? 'primary.main' : 'divider',
              bgcolor: chamberFilter === 'all' ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'primary.main' }
            }}
            onClick={() => setChamberFilter('all')}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {filteredCommittees.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">All Chambers</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: chamberFilter === 'Joint' ? 'primary.main' : 'divider',
              bgcolor: chamberFilter === 'Joint' ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'primary.main' }
            }}
            onClick={() => setChamberFilter('Joint')}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {jointCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">Joint</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: chamberFilter === 'Senate' ? 'error.main' : 'divider',
              bgcolor: chamberFilter === 'Senate' ? alpha(theme.palette.error.main, 0.05) : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'error.main' }
            }}
            onClick={() => setChamberFilter('Senate')}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
              {senateCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">Senate</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: chamberFilter === 'House' ? 'success.main' : 'divider',
              bgcolor: chamberFilter === 'House' ? alpha(theme.palette.success.main, 0.05) : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'success.main' }
            }}
            onClick={() => setChamberFilter('House')}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
              {houseCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">House</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Search filter */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Filter committees by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </Paper>

      {/* Committees List */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {viewMode === 'current' ? 'Current Committees' : 'All Committees'}
                {chamberFilter !== 'all' && ` - ${chamberFilter}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredCommittees.length} committee{filteredCommittees.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <ExportCSVButton
              dataType="committees"
              query={searchQuery || '*'}
              totalResults={filteredCommittees.length}
            />
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <SortableTh col="Committee" sx={{ width: '40%' }} />
                <SortableTh col="Chamber" sx={{ width: '12%' }} />
                <SortableTh col="Type" sx={{ width: '18%' }} />
                <SortableTh col="Members" sx={{ width: '10%' }} />
                <SortableTh col="Status" sx={{ width: '10%' }} />
                <TableCell sx={{ fontWeight: 600, width: '10%', textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((committee) => (
                <TableRow
                  key={committee.CID}
                  sx={{
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <TableCell>
                    <Button
                      component={RouterLink}
                      to={`/committees/${committee.CID}`}
                      sx={{
                        p: 0,
                        minWidth: 'auto',
                        textTransform: 'none',
                        fontWeight: 500,
                        color: getChamberColor(committee.CommitteeChamber),
                        textAlign: 'left',
                        justifyContent: 'flex-start'
                      }}
                    >
                      {committee.CommitteeName}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={committee.CommitteeChamber}
                      size="small"
                      sx={{
                        bgcolor: alpha(getChamberColor(committee.CommitteeChamber), 0.1),
                        color: getChamberColor(committee.CommitteeChamber),
                        fontWeight: 500,
                        height: 22,
                        fontSize: '0.75rem'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {committee.CommitteeType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {committee.CurrentMemberCount || committee.MemberCount || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {committee.Current ? (
                      <Chip label="Current" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                    ) : (
                      <Chip label="Historical" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    <Button
                      component={RouterLink}
                      to={`/committees/${committee.CID}`}
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: 2, minWidth: 60 }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredCommittees.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No committees found matching your filters.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
